import { useCallback, useMemo, useReducer } from 'react'

import { VatSourceCard, type VatReportVm, type VatSourceActions } from '@krp/platform-ui'
import {
  VAT_BATCH_SIZE,
  newOneCOperationId,
  onecVatApply,
  onecVatPreview,
  onecVatSummary,
  type VatItemResult,
} from '@krp/resource-onec-ui'

import { messageOf, useReloadOn, type NomenclatureCardProps } from '../platform/vatCardKit'
import {
  EMPTY_TOTALS,
  INITIAL_EDITOR,
  addResult,
  editorReducer,
  oneCSourceVm,
  reportOf,
  type RunTotals,
} from './oneCVatModel'

/** Карточка ставки НДС базы 1С в «Номенклатура → Настройка»: чтение и массовая запись ставки с предпросмотром. */
export function OneCVatCard({ resource, canWrite, reloadToken }: NomenclatureCardProps) {
  const summary = onecVatSummary.useQuery({ resourceId: resource.id }, { refetchOnMountOrArgChange: true })
  const [previewRequest] = onecVatPreview.useMutation()
  const [applyRequest] = onecVatApply.useMutation()
  const [editor, dispatch] = useReducer(editorReducer, INITIAL_EDITOR)
  useReloadOn(reloadToken, () => void summary.refetch())

  const version = summary.data?.configurationVersion ?? resource.configurationVersion ?? ''

  const preview = useCallback(async () => {
    if (editor.rateCode === '') return
    dispatch({ type: 'previewing' })
    try {
      const answer = await previewRequest({
        resourceId: resource.id,
        expectedConfigurationVersion: version,
        rateCode: editor.rateCode,
        groupKey: editor.groupKey === '' ? null : editor.groupKey,
      }).unwrap()
      dispatch({ type: 'previewed', preview: answer.data })
    } catch (error) {
      dispatch({ type: 'failed', message: messageOf(error) })
    }
  }, [editor.groupKey, editor.rateCode, previewRequest, resource.id, version])

  const apply = useCallback(async () => {
    const plan = editor.preview
    if (plan === null || plan.willChange === 0 || editor.confirmText.trim() !== String(plan.willChange)) return
    dispatch({ type: 'running', total: plan.refKeys.length })
    // Один прогон — один идентификатор: пачка `i` всегда с ключом `vat:<прогон>:<i>`, повтор той же пачки не пишет второй раз.
    const runId = newOneCOperationId()
    let totals: RunTotals = EMPTY_TOTALS
    const items: VatItemResult[] = []
    let stopped: string | null = null
    for (let start = 0; start < plan.refKeys.length; start += VAT_BATCH_SIZE) {
      const chunk = plan.refKeys.slice(start, start + VAT_BATCH_SIZE)
      try {
        const answer = await applyRequest({
          resourceId: resource.id,
          rateCode: plan.rateCode,
          refKeys: chunk,
          identity: {
            operationId: newOneCOperationId(),
            idempotencyKey: `vat:${runId}:${start / VAT_BATCH_SIZE}`,
            expectedConfigurationVersion: version,
          },
        }).unwrap()
        totals = addResult(totals, answer.data)
        items.push(...answer.data.items)
        dispatch({ type: 'progress', done: Math.min(start + chunk.length, plan.refKeys.length) })
        const halted = answer.data.items.find((item) => item.outcome === 'NotProcessed')
        if (halted !== undefined) {
          stopped = halted.message ?? 'Запись остановлена службой'
          // Пачки, до которых не дошли, не отправляются вовсе.
          totals = { ...totals, notProcessed: totals.notProcessed + (plan.refKeys.length - start - chunk.length) }
          break
        }
      } catch (error) {
        // Ответа на пачку нет: часть могла записаться. Товары пачки — «исход неизвестен», остальные не отправлялись.
        const text = messageOf(error)
        stopped = `Связь со службой оборвалась на пачке ${start / VAT_BATCH_SIZE + 1}: ${text}`
        const unknown = chunk.map<VatItemResult>((refKey) => ({ refKey, outcome: 'Unknown', message: text, name: null, article: null }))
        items.push(...unknown)
        totals = {
          ...totals,
          unknown: totals.unknown + chunk.length,
          notProcessed: totals.notProcessed + (plan.refKeys.length - start - chunk.length),
        }
        break
      }
    }
    const report: VatReportVm = reportOf(totals, items, stopped)
    dispatch({ type: 'done', report })
    void summary.refetch()
  }, [applyRequest, editor.confirmText, editor.preview, resource.id, summary, version])

  const actions = useMemo<VatSourceActions>(
    () => ({
      reload: () => void summary.refetch(),
      rateChanged: (code) => dispatch({ type: 'rate', code }),
      groupChanged: (key) => dispatch({ type: 'group', key }),
      preview: () => void preview(),
      confirmChanged: (text) => dispatch({ type: 'confirm', text }),
      apply: () => void apply(),
      cancel: () => dispatch({ type: 'back' }),
      close: () => dispatch({ type: 'back' }),
    }),
    [apply, preview, summary],
  )

  const vm = oneCSourceVm({
    id: resource.id,
    title: resource.displayName,
    badge: '1С',
    summary: summary.data?.data ?? null,
    query: { isLoading: summary.isFetching, isError: summary.isError, errorMessage: summary.error === undefined ? null : messageOf(summary.error) },
    editor,
    canWrite,
  })
  return <VatSourceCard vm={vm} actions={actions} />
}
